import { useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  Link,
  Paper,
  TextField,
  Typography,
  alpha,
} from "@mui/material";
import { Add, Edit as EditIcon } from "@mui/icons-material";
import { useGetList } from "react-admin";
import { Client, ClientCreate, ResourceName } from "@/types";
import Button from "@/components/ui/Button";
import { ButtonStyleType, UIComponentSize } from "@/types/shared/uiComponents";
import { CreateInDialog } from "@/components/common/CreateInDialog";
import { BORDER_RADIUS } from "@/constants/layout";
import { useAppTheme } from "@/hooks/useAppTheme";

interface ClientGateProps {
  /** Whether this document type requires a client */
  requiresClient: boolean;
  /** True when requiresClient is true but no client is set */
  isClientMissing: boolean;
  selectedClient: Client | null;
  onClientChange: (client: Client | null) => void;
  /**
   * Legacy header path (documentReviewRedesignEnabled off): there is no Client
   * cell in that header, so the gate keeps its compact "Client · Change" row
   * once a client is assigned. The redesigned header owns that affordance and
   * passes false (default).
   */
  showAssignedState?: boolean;
  /**
   * "Wrong file? Reject it…" (#7441, artboard 1j). A reviewer who cannot
   * assign a client is often looking at the wrong file; this opens the reject
   * dialog from the place they discover that. Rendered only while the client
   * is missing and only when the owner wires it.
   */
  onRejectRequest?: () => void;
}

/**
 * Top-of-page gate for client selection.
 *
 * When a client is required but missing, this renders a prominent banner
 * with an inline picker — the review grid below is expected to be visually
 * muted until the gate is satisfied.
 *
 * When a client is assigned, renders nothing — the review header's Client cell
 * shows it and opens the picker (#7431 AC1).
 */
export const ClientGate = ({
  requiresClient,
  isClientMissing,
  selectedClient,
  onClientChange,
  onRejectRequest,
  showAssignedState = false,
}: ClientGateProps) => {
  const { theme, isDarkMode } = useAppTheme();
  const [isEditing, setIsEditing] = useState(false);

  if (!requiresClient) return null;

  if (selectedClient && !isEditing) {
    // Redesigned header (#7431 AC1): its Client cell owns display + change, so
    // the gate collapses entirely instead of a duplicate chip row.
    if (!showAssignedState) return null;
    // Legacy header: compact assigned row with a Change action.
    return (
      <Paper
        elevation={0}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          px: 2,
          py: 1,
          mb: 2,
          border: 1,
          borderColor: "divider",
          borderRadius: BORDER_RADIUS.LARGE,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Client
          </Typography>
          <Chip
            label={selectedClient.client_legal_name}
            size="small"
            sx={{ fontWeight: 500, bgcolor: alpha(theme.palette.primary.main, isDarkMode ? 0.2 : 0.08) }}
          />
        </Box>
        <Button
          size={UIComponentSize.Small}
          variant={ButtonStyleType.Text}
          onClick={() => setIsEditing(true)}
          startIcon={<EditIcon sx={{ fontSize: 14 }} />}
        >
          Change
        </Button>
      </Paper>
    );
  }

  // Missing / editing state — prominent banner
  return (
    <Paper
      elevation={0}
      sx={{
        mb: 2,
        p: 2,
        border: 1,
        borderColor: isClientMissing ? "warning.main" : "divider",
        borderRadius: BORDER_RADIUS.LARGE,
        bgcolor: isClientMissing ? alpha(theme.palette.warning.main, isDarkMode ? 0.1 : 0.04) : "background.paper",
      }}
    >
      {isClientMissing && (
        <Alert severity="warning" sx={{ mb: 1.5, py: 0.25, "& .MuiAlert-message": { py: 0.25 } }}>
          <Typography variant="body2">
            Assign a client before approving — the review grid is disabled until a client is set.
          </Typography>
        </Alert>
      )}
      {isClientMissing && onRejectRequest && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
          <Link component="button" type="button" variant="body2" color="text.secondary" onClick={onRejectRequest}>
            Wrong file? Reject it…
          </Link>
        </Box>
      )}
      <InlineClientSelector
        selectedClient={selectedClient}
        onClientChange={(client) => {
          onClientChange(client);
          if (client) setIsEditing(false);
        }}
      />
    </Paper>
  );
};

// ─── Inline selector ────────────────────────────────────────────────────────

export const InlineClientSelector = ({
  selectedClient,
  onClientChange,
  disableClearable = false,
}: {
  selectedClient: Client | null;
  onClientChange: (client: Client | null) => void;
  /**
   * Replace-only mode for a document that already has a client (#7431):
   * "no client" is not a state the review can hold — the tab re-seeds from
   * the record and approve falls back to the original id — so offering a
   * clear that snaps back a moment later only misleads.
   */
  disableClearable?: boolean;
}) => {
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  // Server-side search (#5697) so every client is reachable regardless of tenant
  // size — mirrors the q-filter picker pattern from #5582. Typing drives a `q`
  // query into useGetList instead of loading a capped page and filtering client-side.
  const [searchText, setSearchText] = useState("");

  const { data: clients = [], isLoading } = useGetList(ResourceName.CLIENTS, {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "client_legal_name", order: "ASC" },
    filter: searchText ? { q: searchText } : {},
  });

  // Enable/disable gates on the backend has_active_policy flag (#5697) rather than
  // a separate, capped policies fetch that dropped ~1/3 of policies from the browser.
  const hasActivePolicy = (client: Client) => Boolean(client.has_active_policy);

  // Keep the assigned client present in the options even when it falls outside the
  // freshly-loaded search page, so re-editing doesn't trip MUI's "value not in
  // options" warning and the value stays matchable.
  const options = useMemo(() => {
    if (selectedClient && !clients.some((c) => c.id === selectedClient.id)) {
      return [selectedClient, ...clients];
    }
    return clients;
  }, [clients, selectedClient]);

  return (
    <>
      <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
        <Autocomplete
          size="small"
          sx={{ flex: 1 }}
          disableClearable={disableClearable}
          options={options}
          filterOptions={(x) => x}
          getOptionLabel={(option) => option.client_legal_name || ""}
          getOptionDisabled={(option) => !hasActivePolicy(option)}
          isOptionEqualToValue={(option, val) => option.id === val.id}
          value={selectedClient}
          onChange={(_event, newValue) => {
            if (disableClearable && !newValue) return;
            onClientChange(newValue);
          }}
          // Leave input display uncontrolled so the assigned client's name shows when
          // re-editing; onInputChange only feeds the server `q` filter. Only typed
          // edits set it; clear/blur/reset clear it, so a search abandoned on blur
          // (clearOnBlur empties the box) can't keep narrowing the list on reopen.
          onInputChange={(_event, value, reason) => {
            setSearchText(reason === "input" ? value : "");
          }}
          loading={isLoading}
          noOptionsText={searchText ? "No matches found" : "Type to search..."}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Search clients..."
              variant="outlined"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isLoading && <CircularProgress color="inherit" size={18} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          renderOption={(props, option) => {
            const { key, ...otherProps } = props;
            const hasPolicy = hasActivePolicy(option);
            return (
              <li key={key} {...otherProps}>
                <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%", alignItems: "center" }}>
                  <Typography variant="body2" sx={{ opacity: hasPolicy ? 1 : 0.5 }}>
                    {option.client_legal_name}
                  </Typography>
                  {!hasPolicy && (
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1, whiteSpace: "nowrap" }}>
                      No policy
                    </Typography>
                  )}
                </Box>
              </li>
            );
          }}
        />
        <Button
          startIcon={<Add />}
          onClick={() => setOpenCreateDialog(true)}
          size={UIComponentSize.Small}
          variant={ButtonStyleType.Outlined}
          sx={{ mt: 0.125, flexShrink: 0 }}
        >
          New
        </Button>
      </Box>

      <CreateInDialog<ClientCreate>
        open={openCreateDialog}
        onClose={() => setOpenCreateDialog(false)}
        resource={ResourceName.CLIENTS}
      />
    </>
  );
};
